import { twJoin } from '@tuic/tailwind-config';

import { Button } from '../../../core-components/Button';
import type { FC, ReactNode } from 'react';

import { DashboardTravelItineraryIntroductionProgressBar } from './components/ProgressBar';

import { UniversalCarousel, CarouselStage, CarouselDefaultPagination } from '../../shared/UniversalCarousel';
import {
  SkeletonTextLines,
  Skeleton,
} from '../../../core-components/Skeleton';
import type { ButtonProps } from '../../../core-components/Button';

export type DashboardTravelItineraryIntroductionProps = {
  fallback?: boolean;
  pages?: ReactNode[];
  button?: ButtonProps;
};

/**
 * The `DashboardTravelItineraryIntroduction` component displays an introduction section for a travel itinerary dashboard, including a carousel of pages, progress bar, and action button.
 *
 * ## Props
 *
 * - `fallback`: Optional boolean to render fallback UI.
 * - `pages`: Optional array of ReactNode elements representing the carousel pages.
 * - `button`: Optional ButtonProps for the main action button.
 *
 * ```tsx
 * <DashboardTravelItineraryIntroduction
 *   fallback={false}
 *   pages={[<div>Page 1</div>, <div>Page 2</div>]}
 *   button={{ children: 'Continue', onClick: () => {} }}
 * />
 * ```
 */
export const DashboardTravelItineraryIntroduction = ({
  fallback,
  pages,
  button,
}: DashboardTravelItineraryIntroductionProps) => {
const totalPages = pages?.length ?? 1;

  return (
    <div className="text-marine-high-emphasis text-lg">
      <UniversalCarousel>
        {!fallback && (
          <DashboardTravelItineraryIntroductionProgressBar totalPages={totalPages} />
        )}
        <CarouselStage>
          {fallback ? (
            <div className="mt-8">
              <Skeleton className="h-8 mb-6" />
              <SkeletonTextLines number={10} />
            </div>
          ) : (
            pages?.map((page, index) => <div key={index}>{page}</div>)
          )}
        </CarouselStage>

        <div className="flex flex-col-reverse md:flex-row justify-center md:justify-between gap-8 mt-8 md:mt-10">
          {fallback ? (
            <Button fallback />
          ) : button ? (
            <Button {...button} on="white" variant="primary" />
          ) : null}

          <CarouselDefaultPagination
            className="self-center md:self-auto mt-0"
            centered
          />
        </div>
      </UniversalCarousel>
    </div>
  );
};
